import { checkIsBusinessUser } from "selectors/user";
import { getActiveOrganization } from "selectors/organizations";
import { getContactsData } from "selectors/contacts";
import { getCurrenciesData } from "selectors/metaData";
import {
  getCurrentQuickBooksRealmId,
  getCurrentXeroOrganizationId,
  getSelectedBusinessCategories,
  getSelectedBusinessClasses,
  getSelectedBusinessData,
  getSelectedBusinessItems,
  getSelectedBusinessLocations,
  getSelectedBusinessProjects,
  getSelectedBusinessTaxRates
} from "selectors/businesses";
import { useSelector } from "react-redux";
import Arrays from "utils/Arrays";
import ContactSubTypes from "const/ContactSubTypes";
import Currencies from "const/Currencies";
import DateTimeFormats from "const/DateTimeFormats";
import Dates from "utils/Dates";
import DocumentContext from "contexts/DocumentContext";
import DocumentPaymentTypes from "const/DocumentPaymentTypes";
import Documents from "utils/Documents";
import EditDocumentUtils from "utils/EditDocument";
import IntegrationServicesCategories from "const/IntegrationServicesCategories";
import Money from "utils/Money";
import React, { useCallback, useMemo, useState } from "react";
import moment from "moment";
import parseMoney from "parse-money";

const Provider = (props) => {
  const {
    children,
    rootRef,
    attachment,
    state,
    state: {
      lineItems = [],
      amountBase,
      amountVat,
      paymentType,
      type,
      detailedTaxRates,
      currency
    },
    documentFrozen,
    documentData,
    documentData: { status },
    recogniseData,
    onChange,
    onPairedTransactionClick,
    refetchDocumentData
  } = props;

  const businessUser = useSelector(checkIsBusinessUser);

  const quickBooksBusiness = !!useSelector(getCurrentQuickBooksRealmId);

  const xeroBusiness = !!useSelector(getCurrentXeroOrganizationId);

  const contactsData = useSelector(getContactsData);

  const classes = useSelector(getSelectedBusinessClasses);

  const projects = useSelector(getSelectedBusinessProjects);

  const locations = useSelector(getSelectedBusinessLocations);

  const items = useSelector(getSelectedBusinessItems);

  const taxRates = useSelector(getSelectedBusinessTaxRates);

  const { countryCode } = useSelector(getActiveOrganization);

  const { extraData: { integrationService } = {} } = useSelector(getSelectedBusinessData);

  const [localState, setState] = useState({});

  const currenciesData = useSelector(getCurrenciesData);

  const categories = useSelector(getSelectedBusinessCategories);

  const setLocalState = useCallback((update) => {
    setState((prev) => {
      return { ...prev, ...(typeof update === "function" ? update(prev) : update) };
    });
  }, []);

  const lineItemsValid = useMemo(() => {
    return lineItems.every(({ description, amountTotal = null, item = {}, category = {} }) => {
      return description && amountTotal !== null && (businessUser || item.id || category.code);
    });
  }, [businessUser, lineItems]);

  const setDocumentState = useCallback((update) => {
    onChange((currentState) => {
      return EditDocumentUtils.updateState(typeof update === "function" ? update(currentState) : update, currentState, {
        recogniseData,
        countryCode,
        quickBooksBusiness,
        xeroBusiness,
        integrationService,
        contactsData,
        categories,
        status
      });
    });
  }, [
    onChange,
    recogniseData,
    countryCode,
    quickBooksBusiness,
    xeroBusiness,
    integrationService,
    contactsData,
    categories,
    status
  ]);

  const updateLineItem = useCallback((uuid, fieldName, value) => {
    setDocumentState((prev) => ({
      lineItems: prev.lineItems.map((lineItem) =>
        lineItem.uuid === uuid
          ? EditDocumentUtils.recalculateLineItemFields(
            lineItem, fieldName, value, { xeroBusiness, classes, taxRates, currency }
          )
          : lineItem)
    }));
  }, [setDocumentState, xeroBusiness, classes, taxRates, currency]);

  const lineItemsData = useMemo(() => {
    return Documents.calculateLineItemsData({ lineItems, paymentType, countryCode, quickBooksBusiness, currency });
  }, [countryCode, lineItems, paymentType, quickBooksBusiness, currency]);

  const lineItemsTotalConsistent = useMemo(() => {
    return Documents.checkLineItemsTotalConsistence(state);
  }, [state]);

  const ocrMeta = useMemo(() => {
    const meta = {
      ...(recogniseData?.ocrMeta || {}),
      ...(recogniseData?.ocrMeta?.lineItems ? {
        lineItems: Object.fromEntries(Object.entries(recogniseData.ocrMeta.lineItems).map(([uuid, fields]) => {
          const original = (documentData.lineItems || []).find((lineItem) => lineItem.uuid === uuid);

          const current = lineItems.find((lineItem) => lineItem.uuid === uuid);

          return [uuid, Object.fromEntries(Object.entries(fields).filter(([name]) => {
            return original && current && original[name] === current[name];
          }))];
        }).filter(([, fields]) => Object.keys(fields).length))
      } : {})
    };

    if (!type || !paymentType) return meta;

    const { sender, recipient } = meta.addresses || {};

    return {
      ...meta,
      address: paymentType === DocumentPaymentTypes.BUY ? sender?.name : recipient?.name
    };
  }, [documentData.lineItems, lineItems, paymentType, recogniseData?.ocrMeta, type]);

  const filteredContactsData = useMemo(() => {
    const buyPaymentType = paymentType === DocumentPaymentTypes.BUY;

    const onlyVendors = paymentType && buyPaymentType && integrationService && !xeroBusiness;

    const onlyCustomers = paymentType && !buyPaymentType && integrationService && !xeroBusiness;

    return contactsData.filter(({ subType }) => {
      return !subType || (!!onlyCustomers === !!onlyVendors)
        || (onlyCustomers && subType === ContactSubTypes.CUSTOMER) || (onlyVendors && subType === ContactSubTypes.VENDOR);
    });
  }, [contactsData, integrationService, paymentType, xeroBusiness]);

  const filteredItems = useMemo(() => {
    return items.filter(({ salesItem, purchaseItem }) => {
      return !xeroBusiness || (paymentType === DocumentPaymentTypes.BUY ? purchaseItem : salesItem);
    });
  }, [items, paymentType, xeroBusiness]);

  const filteredCategories = useMemo(() => {
    return categories.filter(({ group }) => {
      return !paymentType || IntegrationServicesCategories.checkIsAvailable(integrationService, group, paymentType);
    });
  }, [categories, integrationService, paymentType]);

  const normalizeFieldValue = useCallback((fieldName, text) => {
    switch (fieldName) {
      case "amount":
      case "amountVat":
      case "amountBase":
      case "detailedTaxRate": {
        if (currency === Currencies.KWD) {
          const parsedKwdAmount = Money.parseKwdAmount(text);

          if (parsedKwdAmount !== null) return parsedKwdAmount;
        }

        let parsed = parseMoney(text);

        if (!parsed?.currency && currency) {
          parsed = parseMoney(`${text} ${currency}`);
        }

        return parsed?.amount || "";
      }
      case "quantity":
      case "vatRate":
        return parseFloat(text);
      case "address": {
        const lowercased = text && text.toLocaleLowerCase();

        return filteredContactsData.find(({ name }) => name && (name.toLocaleLowerCase() === lowercased)) || { name: text };
      }
      case "class": {
        const lowercased = text && text.toLocaleLowerCase();

        return classes.find(({ name }) => name && (name.toLocaleLowerCase() === lowercased)) || { name: text };
      }
      case "project": {
        const lowercased = text && text.toLocaleLowerCase();

        return projects.find(({ name }) => name && (name.toLocaleLowerCase() === lowercased)) || { name: text };
      }
      case "item": {
        const lowercased = text && text.toLocaleLowerCase();

        return filteredItems.find(({ name }) => name && (name.toLocaleLowerCase() === lowercased)) || { name: text };
      }
      case "location": {
        const lowercased = text && text.toLocaleLowerCase();

        return locations.find(({ name }) => name && (name.toLocaleLowerCase() === lowercased)) || { name: text };
      }
      case "category": {
        const lowercased = text && text.toLocaleLowerCase();

        return filteredCategories
          .find(({ displayName }) => displayName && (displayName.toLocaleLowerCase() === lowercased)) || { name: text };
      }
      case "issueDate":
      case "dueDate":
      case "paymentDate": {
        const momentDate = moment(Dates.parseDate(text, countryCode, currency));

        return momentDate.isValid() ? momentDate.format(DateTimeFormats.API_DATE) : "";
      }
      case "currency": {
        let parsed = parseMoney(text);

        let found = Arrays.findByKey(currenciesData, "code", parsed?.currency);

        if (found) return parsed?.currency;
        parsed = parseMoney(`1 ${text}`);
        found = Arrays.findByKey(currenciesData, "code", parsed?.currency);

        return found ? parsed?.currency : null;
      }
      default:
        return text;
    }
  }, [
    classes,
    countryCode,
    currenciesData,
    currency,
    filteredCategories,
    filteredContactsData,
    filteredItems,
    locations,
    projects
  ]);

  const setValueFromDocument = useCallback((fieldId, text) => {
    const [firstPart, secondPart] = fieldId.split(".") || [];

    const value = normalizeFieldValue(firstPart, text);

    if (value !== null) {
      if (firstPart === "detailedTaxRate") {
        setDocumentState({
          detailedTaxRates: Arrays.updateItemById(detailedTaxRates, secondPart, (prev) => {
            return { ...prev, value };
          })
        });
      } else if (secondPart) {
        updateLineItem(secondPart, firstPart, value);
      } else {
        setDocumentState({ [firstPart]: value });
      }
    }
  }, [detailedTaxRates, normalizeFieldValue, setDocumentState, updateLineItem]);

  const contextValue = {
    lineItemsAmountGreaterThanDocument: lineItemsData.amountBase > amountBase,
    lineItemsAmountLessThanDocument: lineItemsData.amountBase < amountBase,
    lineItemsAmountVatGreaterThanDocument: !detailedTaxRates.length && lineItemsData.amountVat > amountVat,
    lineItemsAmountVatLessThanDocument: !detailedTaxRates.length && lineItemsData.amountVat < amountVat,
    lineItemsTotalConsistent,
    lineItemsValid,
    localState,
    rootRef,
    documentData,
    recogniseData,
    attachment,
    documentState: state,
    ocrMeta,
    documentFrozen,
    setLocalState,
    updateLineItem,
    setDocumentState,
    setValueFromDocument,
    onPairedTransactionClick,
    refetchDocumentData
  };

  return (
    <DocumentContext.Provider value={contextValue}>
      {children}
    </DocumentContext.Provider>
  );
};

export default Provider;
